import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { formatTotalWeeklyMiles, mileageRowTruckLabel } from '../../utils/weeklyChecklistMiles';
import { formatTarpCell, formatTarpTotal, sumTarps } from '../../utils/tarpMetrics';

const COMPANY_FALLBACK = 'Kranawetter Transport';
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function esc(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function formatDay(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function dayMs(value, endOfDay) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    if (endOfDay) d.setHours(23, 59, 59, 999);
    else d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function personId(checklist) {
    const person = checklist?.submittedBy;
    if (!person) return '';
    if (typeof person === 'object') return String(person._id || person.id || '');
    return String(person);
}

function truckLine(truck) {
    if (!truck || typeof truck !== 'object') return '';
    const unit = truck.unitNumber || truck.truckNumber || '';
    const rest = [truck.make, truck.model].filter(Boolean).join(' ');
    const year = truck.year ? `(${truck.year})` : '';
    return [unit, rest, year].filter(Boolean).join(' ');
}

function companyName(checklist) {
    const name = checklist?.company?.name;
    return name ? String(name) : COMPANY_FALLBACK;
}

function sumInts(values) {
    return (values || []).reduce((total, value) => total + (parseInt(value, 10) || 0), 0);
}

function shortRunCell(metrics, index) {
    const yesNo = (metrics.shortRunYes || [])[index];
    const orders = (metrics.shortRunOrders || [])[index] || [];
    const legacy = (metrics.shortRunDelivering || [])[index];
    if (yesNo) {
        if (yesNo === 'yes') return orders.length ? orders.join(', ') : 'Yes';
        return 'No';
    }
    if (legacy) return parseInt(legacy, 10) > 0 ? 'Yes' : 'No';
    return '—';
}

function shortRunTotal(metrics) {
    return (metrics.shortRunOrders || []).reduce((total, orders) => {
        if (!Array.isArray(orders)) return total;
        return total + orders.filter((order) => /\d+/.test(String(order))).length;
    }, 0);
}

function metricRow(label, hint, cells, total) {
    return `<tr>
        <td class="metric">${esc(label)}${hint ? `<div class="hint">${esc(hint)}</div>` : ''}</td>
        ${cells.map((cell) => `<td class="center">${esc(cell)}</td>`).join('')}
        <td class="center total">${esc(total)}</td>
    </tr>`;
}

function table(headers, rows, numericFrom) {
    if (!rows.length) return '';
    return `<table>
        <thead><tr>${headers.map((header, index) => `<th class="${index >= numericFrom ? 'num' : ''}">${esc(header)}</th>`).join('')}</tr></thead>
        <tbody>${rows.join('')}</tbody>
    </table>`;
}

function sheetHtml(checklist, index, total, logo) {
    const company = companyName(checklist);
    const driver = checklist.submittedBy?.name || 'Unknown driver';
    const email = checklist.submittedBy?.email || '';
    const truck = truckLine(checklist.truck);
    const trailer = truckLine(checklist.trailer);
    const week = `${formatDay(checklist.weekStartDate)} – ${formatDay(checklist.weekEndDate)}`;
    const miles = Array.isArray(checklist.truckMileages) ? checklist.truckMileages : [];

    let milesTable = '';
    if (miles.length) {
        const rows = miles.map((row, rowIndex) => {
            const leg = Number(row.mileageEnd) - Number(row.mileageStart);
            return `<tr>
                <td>${esc(mileageRowTruckLabel(row, rowIndex, checklist))}</td>
                <td class="num">${esc(row.mileageStart ?? '—')}</td>
                <td class="num">${esc(row.mileageEnd ?? '—')}</td>
                <td class="num">${Number.isFinite(leg) ? esc(leg.toFixed(1)) : '—'}</td>
            </tr>`;
        });
        milesTable = table(['Truck', 'Start', 'End', 'Miles'], rows, 1);
    } else if (checklist.mileageStart != null || checklist.mileageEnd != null) {
        const leg = Number(checklist.mileageEnd) - Number(checklist.mileageStart);
        milesTable = table(
            ['Truck', 'Start', 'End', 'Miles'],
            [`<tr>
                <td>${esc(truck || 'Truck')}</td>
                <td class="num">${esc(checklist.mileageStart ?? '—')}</td>
                <td class="num">${esc(checklist.mileageEnd ?? '—')}</td>
                <td class="num">${Number.isFinite(leg) ? esc(leg.toFixed(1)) : '—'}</td>
            </tr>`],
            1
        );
    }

    const facts = [];
    if (typeof checklist.oversizeLoadedMiles === 'number' && checklist.oversizeLoadedMiles > 0) {
        facts.push(['Loaded oversize miles', String(checklist.oversizeLoadedMiles)]);
    }
    if (typeof checklist.dotInspectionCompleted === 'boolean') {
        facts.push(['DOT inspection', checklist.dotInspectionCompleted ? 'Completed' : 'Not completed']);
    }
    if (checklist.hasReimbursement) facts.push(['Reimbursements', 'Yes']);
    if (typeof checklist.cashReceiptsAmount === 'number' && checklist.cashReceiptsAmount > 0) {
        facts.push(['Cash receipts', `$${checklist.cashReceiptsAmount.toFixed(2)}`]);
    }
    if (checklist.weekendPay === 'half' || checklist.weekendPay === 'full') {
        facts.push(['Weekend pay', checklist.weekendPay === 'half' ? 'Half weekend' : 'Full weekend']);
    }
    if (Array.isArray(checklist.cashReceiptsAttachments) && checklist.cashReceiptsAttachments.length) {
        facts.push(['Receipts', 'On file']);
    }

    const factsHtml = facts.length
        ? `<div class="facts">${facts.map(([label, value]) => `<div class="fact"><div class="k">${esc(label)}</div><div class="v">${esc(value)}</div></div>`).join('')}</div>`
        : '';

    const note = checklist.cashReceiptsNote
        ? `<p class="plain"><span class="k">Receipt note.</span> ${esc(checklist.cashReceiptsNote)}</p>`
        : '';

    const breakdowns = Array.isArray(checklist.breakdownEvents) ? checklist.breakdownEvents : [];
    const breakdownHtml = (checklist.hasBreakdownPay || breakdowns.length)
        ? `<h2>Breakdown pay</h2>
            <p class="plain">${checklist.hasBreakdownPay ? 'Breakdown pay this week.' : 'No breakdown pay claimed.'}</p>
            ${table(
                ['Date', 'Time of notification'],
                breakdowns.map((event) => `<tr><td>${esc(formatDay(event?.date))}</td><td>${esc(event?.notificationTime || '—')}</td></tr>`),
                99
            )}`
        : '';

    const detentions = Array.isArray(checklist.detentionEvents) ? checklist.detentionEvents : [];
    const detentionHtml = detentions.length
        ? `<h2>Detention</h2>${table(
            ['Date', 'Location', 'Arrival', 'Depart'],
            detentions.map((event) => `<tr>
                <td>${esc(formatDay(event?.date))}</td>
                <td>${esc(event?.location || '—')}</td>
                <td>${esc(event?.arrivalTime || '—')}</td>
                <td>${esc(event?.departTime || '—')}</td>
            </tr>`),
            99
        )}`
        : '';

    const dispatches = Array.isArray(checklist.weekendDispatchDates) ? checklist.weekendDispatchDates : [];
    const dispatchHtml = dispatches.length && checklist.weekendPay !== 'half' && checklist.weekendPay !== 'full'
        ? `<h2>Weekend dispatch</h2><p class="plain">${esc(dispatches.map((date) => formatDay(date)).join('  ·  '))}</p>`
        : '';

    const loads = Array.isArray(checklist.weeklyLoads) ? checklist.weeklyLoads : [];
    const loadsHtml = loads.length
        ? `<h2>Weekly loads</h2>${table(
            ['Date', 'ELD order #', 'Pickup', 'Delivery'],
            loads.map((row) => `<tr>
                <td>${esc(formatDay(row?.date))}</td>
                <td>${esc(row?.eldOrderNumber || '—')}</td>
                <td>${esc(row?.from || '—')}</td>
                <td>${esc(row?.to || '—')}</td>
            </tr>`),
            99
        )}`
        : '';

    let metricsHtml = '';
    if (checklist.dailyMetrics) {
        const metrics = checklist.dailyMetrics;
        const days = (values, format) => Array.from({ length: 7 }, (_, day) => format((values || [])[day], day));
        const rows = [
            metricRow('Nights out', '', days(metrics.nightsOut, (value) => (value ? '✓' : '–')), String((metrics.nightsOut || []).filter(Boolean).length)),
            metricRow('Tarps', '', days(metrics.tarps, (value) => formatTarpCell(value)), formatTarpTotal(sumTarps(metrics.tarps))),
            metricRow('Extra drop / pickup', 'Additional docks for TG lanes', days(metrics.extraDropPickup, (value) => value || '–'), String(sumInts(metrics.extraDropPickup))),
            metricRow('WW wood cabinet drops', '', days(metrics.wwWoodCabinetDrops, (value) => value || '–'), String(sumInts(metrics.wwWoodCabinetDrops))),
            metricRow('Pre-load, not delivering', 'Pickup and drop within 160 miles of the yard', days(metrics.preLoadNotDelivering, (value) => value || '–'), String(sumInts(metrics.preLoadNotDelivering))),
            metricRow('Oversize / truss', 'With permits', days(metrics.oversizeTruss, (value) => (value ? '✓' : '–')), String((metrics.oversizeTruss || []).filter(Boolean).length)),
            metricRow('Short run, delivering', 'Pickup and delivery within 325 miles', days(null, (_, day) => shortRunCell(metrics, day)), String(shortRunTotal(metrics))),
        ];
        metricsHtml = `<h2>Weekly metrics</h2>
            <p class="pay-note">Entries below must be recorded accurately. Unrecorded items will not be reflected on paychecks.</p>
            <table class="metrics">
                <thead><tr><th>Metric</th>${DAYS.map((day) => `<th class="center">${day}</th>`).join('')}<th class="center">Total</th></tr></thead>
                <tbody>${rows.join('')}</tbody>
            </table>`;
    }

    const comments = checklist.additionalNotes
        ? `<h2>Comments</h2><p class="comments">${esc(checklist.additionalNotes)}</p>`
        : '';

    const reviewed = checklist.reviewedBy?.name
        ? `<div class="meta">Reviewed by ${esc(checklist.reviewedBy.name)}${checklist.reviewedAt ? ` on ${esc(formatDay(checklist.reviewedAt))}` : ''}</div>`
        : '';

    return `<section class="sheet">
        <header>
            <img src="${esc(logo)}" alt="${esc(company)}" />
            <div class="brand">
                <div class="company">${esc(company)}</div>
                <div class="doctype">Weekly Checklist</div>
            </div>
            <div class="week">${esc(week)}</div>
        </header>
        <h1>${esc(driver)}</h1>
        ${email ? `<div class="email">${esc(email)}</div>` : ''}
        <div class="identity">
            ${truck ? `<div><span>Truck</span>${esc(truck)}</div>` : ''}
            ${trailer ? `<div><span>Trailer</span>${esc(trailer)}</div>` : ''}
        </div>
        ${reviewed}
        ${milesTable ? `<h2>Odometer</h2>${milesTable}<p class="total-miles">Total miles <strong>${esc(formatTotalWeeklyMiles(checklist))} mi</strong></p>` : ''}
        ${factsHtml || note ? `<h2>Operations</h2>${factsHtml}${note}` : ''}
        ${breakdownHtml}
        ${detentionHtml}
        ${dispatchHtml}
        ${loadsHtml}
        ${metricsHtml}
        ${comments}
        <footer>
            <span>${esc(company)}</span>
            <span>Printed ${esc(formatDay(new Date()))}</span>
            <span>${index + 1} of ${total}</span>
        </footer>
    </section>`;
}

function packetHtml(checklists) {
    const logo = `${window.location.origin}${process.env.PUBLIC_URL || ''}/portal-logo.png`;
    const sheets = checklists.map((checklist, index) => sheetHtml(checklist, index, checklists.length, logo)).join('');
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Weekly Checklists</title>
<style>
  @page { size: letter; margin: 0.5in; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { background: #fff; color: #1c1917; color-scheme: light; }
  body { margin: 0; font-family: "Segoe UI", "Helvetica Neue", Arial, sans-serif; font-size: 12px; }
  .sheet { break-after: page; page-break-after: always; }
  .sheet:last-child { break-after: auto; page-break-after: auto; }
  header { display: flex; align-items: flex-end; gap: 16px; padding-bottom: 10px; border-bottom: 2px solid #E94F37; }
  header img { height: 36px; width: auto; }
  .brand { flex: 1; }
  .company { font-size: 16px; font-weight: 700; letter-spacing: -0.01em; line-height: 1.1; }
  .doctype { margin-top: 2px; font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: #78716c; }
  .week { font-size: 12px; font-weight: 600; color: #44403c; text-align: right; }
  h1 { margin: 14px 0 0; font-size: 22px; line-height: 1.15; letter-spacing: -0.02em; }
  .email { margin-top: 2px; color: #78716c; font-size: 11px; }
  .identity { display: flex; gap: 28px; margin-top: 8px; font-size: 12px; }
  .identity span { display: block; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #78716c; }
  .meta { margin-top: 6px; font-size: 11px; color: #57534e; }
  h2 { margin: 16px 0 6px; padding-bottom: 3px; border-bottom: 1.5px solid #E94F37; font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: #57534e; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #57534e; background: #f6f3ef; padding: 5px 8px; border-bottom: 1px solid #e7e0d8; font-weight: 700; }
  td { padding: 5px 8px; border-bottom: 1px solid #f0ebe6; vertical-align: top; }
  th.num, td.num { text-align: right; }
  .center { text-align: center; }
  td.total { font-weight: 700; }
  .metric { font-weight: 600; }
  .hint { font-weight: 400; color: #78716c; font-size: 9px; }
  .metrics { font-size: 10px; }
  .metrics td, .metrics th { padding: 4px 4px; }
  .total-miles { margin: 6px 0 0; text-align: right; color: #44403c; }
  .facts { display: flex; flex-wrap: wrap; gap: 8px 18px; }
  .fact .k { font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #78716c; }
  .fact .v { font-weight: 700; }
  .plain { margin: 4px 0 0; }
  .plain .k { font-weight: 700; }
  .pay-note { margin: 0 0 6px; font-size: 10px; color: #9a3412; }
  .comments { margin: 0; white-space: pre-wrap; }
  footer { display: flex; justify-content: space-between; gap: 12px; margin-top: 18px; padding-top: 8px; border-top: 1px solid #e7e0d8; font-size: 9px; letter-spacing: 0.06em; text-transform: uppercase; color: #78716c; }
</style>
</head>
<body onload="window.print()">${sheets}</body>
</html>`;
}

export function buildChecklistPacketHtml(checklists) {
    return packetHtml((checklists || []).filter(Boolean));
}

export function printWeeklyChecklists(checklists) {
    const rows = (checklists || []).filter(Boolean);
    if (!rows.length) return;
    const win = window.open('', '_blank');
    if (!win) {
        window.alert('Allow pop-ups to print checklists.');
        return;
    }
    win.document.open();
    win.document.write(buildChecklistPacketHtml(rows));
    win.document.close();
}

export function selectChecklists({ checklists, scope, initialChecklist, driverId, from, to }) {
    if (scope === 'this') return initialChecklist ? [initialChecklist] : [];
    const fromMs = dayMs(from, false);
    const toMs = dayMs(to, true);
    return (checklists || [])
        .filter((checklist) => {
            if (scope === 'driver' && personId(checklist) !== String(driverId || '')) return false;
            const start = new Date(checklist.weekStartDate).getTime();
            if (fromMs != null && start < fromMs) return false;
            if (toMs != null && start > toMs) return false;
            return true;
        })
        .slice()
        .sort((a, b) => {
            const byName = String(a.submittedBy?.name || '').localeCompare(String(b.submittedBy?.name || ''));
            if (byName) return byName;
            return new Date(a.weekStartDate) - new Date(b.weekStartDate);
        });
}

export function WeeklyChecklistPrintDialog({
    open,
    onClose,
    checklists,
    initialChecklist,
    defaultFrom,
    defaultTo,
}) {
    const [scope, setScope] = useState(initialChecklist ? 'this' : 'everyone');
    const [driverId, setDriverId] = useState('');
    const [from, setFrom] = useState(null);
    const [to, setTo] = useState(null);
    const wasOpen = useRef(false);

    const drivers = useMemo(() => {
        const seen = new Map();
        (checklists || []).forEach((checklist) => {
            const id = personId(checklist);
            if (!id || seen.has(id)) return;
            seen.set(id, checklist.submittedBy?.name || 'Unknown');
        });
        return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [checklists]);

    useEffect(() => {
        if (!open) {
            wasOpen.current = false;
            return;
        }
        if (wasOpen.current) return;
        wasOpen.current = true;
        setScope(initialChecklist ? 'this' : 'everyone');
        const seeded = personId(initialChecklist);
        setDriverId(seeded || drivers[0]?.id || '');
        const week = initialChecklist?.weekStartDate ? new Date(initialChecklist.weekStartDate) : null;
        setFrom(defaultFrom ? new Date(defaultFrom) : week);
        setTo(defaultTo ? new Date(defaultTo) : week);
    }, [open, initialChecklist, defaultFrom, defaultTo, drivers]);

    const rows = selectChecklists({ checklists, scope, initialChecklist, driverId, from, to });
    const pages = rows.length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Print checklists</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    <RadioGroup value={scope} onChange={(event) => setScope(event.target.value)}>
                        <FormControlLabel value="this" control={<Radio />} label="This checklist" disabled={!initialChecklist} />
                        <FormControlLabel value="driver" control={<Radio />} label="One driver" />
                        <FormControlLabel value="everyone" control={<Radio />} label="Everyone" />
                    </RadioGroup>
                    {scope === 'driver' && (
                        <FormControl size="small" fullWidth>
                            <InputLabel>Driver</InputLabel>
                            <Select label="Driver" value={driverId} onChange={(event) => setDriverId(event.target.value)}>
                                {drivers.map((driver) => (
                                    <MenuItem key={driver.id} value={driver.id}>{driver.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    {scope !== 'this' && (
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <DatePicker label="Week start from" value={from} onChange={setFrom} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                                <DatePicker label="Week start to" value={to} onChange={setTo} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
                            </Stack>
                        </LocalizationProvider>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {pages === 0 ? 'Nothing matches. Widen the dates or pick another driver.' : `${pages} checklist${pages === 1 ? '' : 's'} · ${pages} page${pages === 1 ? '' : 's'}`}
                    </Typography>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={pages === 0}
                    onClick={() => {
                        printWeeklyChecklists(rows);
                        onClose();
                    }}
                >
                    Print
                </Button>
            </DialogActions>
        </Dialog>
    );
}
